<div class="min-h-0"
     [class.dc-dossier-materialize]="decryptLanding.materialize()">

  <!-- Gold engram gate: open → search panel (empty search; chips = same multi-name as site) -->
  <dc-decrypt-landing
    *ngIf="selectedPlayers.length === 0 && !textView.enabled()"
    mode="idle"
    [searchUsername]="searchUsername"
    [searching]="!!loading['search']"
    [selectedNames]="searchChips"
    (searchUsernameChange)="searchUsername = $event; onSearchInput($event)"
    (queueName)="addSearchChip()"
    (removeName)="removeSearchChip($event)"
    [errorMessage]="errorMessage"
    (search)="addPlayer()"
    (favorites)="onEngramFavorites()"
    (searchPanelOpen)="onEngramSearchPanelOpen()">
  </dc-decrypt-landing>

  <!-- Load / unzip overlay (fresh search decrypt → hard-light dossier) -->
  <dc-decrypt-landing
    *ngIf="decryptLanding.visible() && selectedPlayers.length > 0"
    mode="loading"
    [progress]="getOverallProgress()"
    [caption]="decryptingCaption"
    [subline]="decryptingSubline"
    [guardianLabel]="selectedPlayers[0].displayName || ''"
    [selectedNames]="decryptLandingSelectedNames"
    [litAccounts]="getLitAccountCount()"
    [totalAccounts]="decryptLandingAccountTotal"
    [accountStatuses]="accountLoadingStatuses"
    [unzipping]="decryptLanding.phase() === 'unzipping'"
    (skip)="onDecryptLandingSkip()"
    (unzipComplete)="onDecryptLandingUnzipComplete()">
  </dc-decrypt-landing>

  <!-- Offline archive banner -->
  <div *ngIf="isOfflineArchiveMode && offlineFrozenLabel" class="w-full px-4 sm:px-6 lg:px-8 pt-4">
    <div class="destiny-banner-success flex flex-wrap items-center justify-between gap-3">
      <p class="text-sm text-emerald-100 flex-1">
        <ng-container *ngIf="!isArchiveSyncing">
          {{ uiI18n.t('archive.offlineBanner') }} {{ offlineFrozenLabel }}.
        </ng-container>
        <ng-container *ngIf="isArchiveSyncing">
          {{ archiveProgressMessage || uiI18n.t('archive.checkingUpdates') }}
        </ng-container>
      </p>
      <div class="flex flex-wrap gap-2 shrink-0">
        <button type="button"
                [disabled]="archiveExporting"
                (click)="checkForArchiveUpdates()"
                class="text-xs px-3 py-1.5 destiny-btn-primary disabled:opacity-50">
          {{ uiI18n.t('archive.checkUpdates') }}
        </button>
        <button type="button"
                [disabled]="archiveExporting"
                (click)="exitOfflineArchive()"
                class="text-xs px-3 py-1.5 destiny-btn-secondary disabled:opacity-50">
          {{ uiI18n.t('archive.exitOffline') }}
        </button>
      </div>
    </div>
  </div>

  <!-- Offline ready success -->
  <div *ngIf="showOfflineDeviceReady && !dismissOfflineDeviceReady && !archiveExporting"
       class="w-full px-4 sm:px-6 lg:px-8 pt-4">
    <div class="destiny-banner-success flex flex-wrap items-start justify-between gap-3">
      <div class="flex-1 min-w-0">
        <p class="text-sm text-emerald-100">{{ uiI18n.t('archive.deviceReady') }}</p>
        <p *ngIf="showInstallAppHint" class="text-xs text-emerald-200/90 mt-2">{{ uiI18n.t('archive.installAppHint') }}</p>
      </div>
      <button type="button" (click)="dismissOfflineReadyBanner()"
              class="text-emerald-300 hover:text-white p-1 shrink-0" aria-label="Dismiss">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
      </button>
    </div>
  </div>

  <!-- Archive export progress -->
  <div *ngIf="archiveExporting" class="w-full px-4 sm:px-6 lg:px-8 pt-4">
    <div class="destiny-surface-panel p-3">
      <p class="text-sm text-slate-200 mb-2">{{ archiveProgressMessage || uiI18n.t('archive.exportProgress') }}</p>
      <div class="h-1.5 destiny-progress-track">
        <div class="destiny-progress-fill h-full" [style.width.%]="archiveProgressPercent"></div>
      </div>
    </div>
  </div>

  <input id="archive-import-input" type="file" accept=".zip,.chronicle.zip,application/zip" class="hidden"
         (change)="onArchiveFileSelected($event)" />

  <div *ngIf="showBackgroundLoadingTip" class="w-full px-4 sm:px-6 lg:px-8 pt-4">
    <div class="bg-amber-900/40 border border-amber-600/50 rounded-lg p-3 flex items-center justify-between gap-3">
      <p class="text-sm text-amber-200 flex-1">
        Loading may have been slower while this tab was in the background. Keep this tab visible for fastest loading.
      </p>
      <button (click)="dismissBackgroundLoadingTip()" class="text-amber-300 hover:text-white p-1 rounded transition-colors shrink-0" aria-label="Dismiss">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
      </button>
    </div>
  </div>

  <!-- Dossier UI only after a Guardian is selected (gate covers empty state) -->
  <div class="w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6"
       *ngIf="selectedPlayers.length > 0 || textView.enabled()">
    <div class="dc-content-shell">
      <header *ngIf="selectedPlayers.length > 0"
              class="dc-dossier-hdr"
              data-dc-engram-target="accounts">
        <div class="dc-dossier-hdr__row1">
          <div class="dc-dossier-hdr__badge" aria-hidden="true"></div>
          <div class="dc-dossier-hdr__title">
            <h1>{{ dossierHeaderTitle }}</h1>
            <div class="dc-dossier-hdr__label">{{ dossierHeaderSubtitle }}</div>
          </div>
          <button type="button"
                  class="dc-dossier-hdr__chip dc-dossier-hdr__chip-add"
                  (click)="searchPanelExpanded ? collapseSearchPanel() : expandSearchPanel()">
            {{ searchPanelExpanded ? 'Close search' : '+ Decrypt another' }}
          </button>
          <button type="button"
                  (click)="openFavoritesModal()"
                  class="chronicle-toolbar-btn chronicle-toolbar-btn--primary text-xs px-3 py-1.5 shrink-0">
            ★ Favorites
          </button>
        </div>

        <div class="dc-dossier-hdr__row2">
          <div class="dc-dossier-hdr__stats-col">
            <div class="dc-dossier-hdr__stats dc-dossier-hdr__stats--primary">
              <div class="dc-dossier-hdr__stat"
                   title="Time in matches, strikes, raids, and other activities. Excludes orbit, Tower, and social spaces.">
                <span class="dc-dossier-hdr__label">Active time</span>
                <div class="dc-dossier-hdr__stat-v">{{ dossierHeaderActiveTimeLabel }}</div>
              </div>
              <div class="dc-dossier-hdr__stat"
                   title="Bungie character playtime that is not in an activity — orbit, Tower, and social spaces.">
                <span class="dc-dossier-hdr__label">Orbit &amp; social</span>
                <div class="dc-dossier-hdr__stat-v">{{ dossierHeaderSocialTimeLabel }}</div>
              </div>
              <div class="dc-dossier-hdr__stat">
                <span class="dc-dossier-hdr__label">Activities</span>
                <div class="dc-dossier-hdr__stat-v">{{ dossierHeaderActivityCount }}</div>
              </div>
              <div class="dc-dossier-hdr__stat">
                <span class="dc-dossier-hdr__label">Seals</span>
                <div class="dc-dossier-hdr__stat-v">{{ dossierHeaderSealCount }}</div>
              </div>
            </div>
            <div class="dc-dossier-hdr__stats dc-dossier-hdr__stats--classes"
                 title="Bungie character playtime by class, summed across every searched account (includes orbit).">
              <div class="dc-dossier-hdr__stat" *ngFor="let cls of dossierHeaderClassTimes">
                <span class="dc-dossier-hdr__label dc-dossier-hdr__label--class">
                  <img *ngIf="getClassIconUrl(cls.name) as icon"
                       [src]="icon"
                       class="dc-dossier-hdr__class-icon"
                       [alt]="cls.name"
                       [title]="cls.name" />
                  {{ cls.name }}
                </span>
                <div class="dc-dossier-hdr__stat-v dc-dossier-hdr__stat-v--sm">{{ cls.label }}</div>
              </div>
            </div>
          </div>
          <div class="dc-dossier-hdr__otd"
               [class.is-open]="onThisDayExpanded"
               data-dc-engram-target="date">
            <div class="dc-dossier-hdr__otd-top">
              <div *ngIf="dbReady" class="dc-dossier-hdr__date">
                <button type="button" (click)="nudgeHeaderDate(-1)" aria-label="Previous day">‹</button>
                <app-date-picker [selectedMonth]="selectedMonth"
                                 [selectedDay]="selectedDay"
                                 (dateChange)="onDatePickerChange($event)"></app-date-picker>
                <button type="button" (click)="nudgeHeaderDate(1)" aria-label="Next day">›</button>
                <button type="button" (click)="goHeaderToday()">Today</button>
                <button type="button"
                        class="dc-dossier-hdr__date-search"
                        (click)="onDateSearch()">
                  Search Date
                </button>
              </div>
              <span class="dc-dossier-hdr__otd-title">On This Day</span>
              <button type="button"
                      class="dc-dossier-hdr__otd-sum"
                      (click)="toggleOnThisDay()"
                      [attr.aria-expanded]="onThisDayExpanded">{{ onThisDaySummary }}</button>
              <button type="button"
                      class="chronicle-toolbar-btn chronicle-toolbar-btn--secondary text-xs shrink-0 hidden sm:inline-flex"
                      (click)="showShareDropdown = true">
                Share &amp; Archive
              </button>
              <button type="button"
                      class="chronicle-toolbar-btn chronicle-toolbar-btn--secondary dc-date-more sm:hidden"
                      (click)="toggleDateOverflow(); showShareDropdown = true"
                      aria-label="Share and archive">
                ⋯
              </button>
            </div>
            <div class="dc-dossier-hdr__yrs">
              <div *ngFor="let card of headerOnThisDayYears" class="dc-dossier-hdr__yr">
                <div class="dc-dossier-hdr__yr-y">{{ card.year }}</div>
                <div class="dc-dossier-hdr__yr-d">
                  <ng-container *ngIf="card.game === 'D1'">D1 · </ng-container>{{ card.season || '—' }}
                </div>
                <div class="dc-dossier-hdr__yr-a">{{ card.line }}</div>
              </div>
              <button type="button" class="dc-dossier-hdr__more" (click)="scrollToActivitiesList()">See all ›</button>
            </div>
          </div>
        </div>

        <div class="dc-dossier-hdr__accounts">
          <p class="dc-dossier-hdr__accounts-hint">Click a card to filter the whole dossier. Click again to include it again. No selection = all accounts.</p>
          <app-account-card-grid
            [perPlatformStats]="perPlatformStats"
            [selectedAccountKeys]="selectedAccountKeysForActivities"
            [statsPending]="accountStatsPending"
            (accountKeyToggle)="onSiteAccountCardToggle($event)">
          </app-account-card-grid>
        </div>

        <div *ngIf="searchPanelExpanded" class="dc-dossier-hdr__search">
          <div *ngIf="!apiAvailable && !d1ApiIssue && !d2ApiIssue" class="bg-red-900 text-red-200 p-2 rounded mb-3 text-sm">
            Bungie API is currently unavailable. Showing cached data where possible.
          </div>
          <div *ngIf="d1ApiIssue" class="bg-amber-950/80 text-amber-100 border border-amber-600/50 p-3 rounded mb-3 text-sm">
            <p class="font-medium text-amber-200 mb-0.5">Destiny 1 API issue</p>
            <p>{{ d1ApiIssue }}</p>
          </div>
          <div *ngIf="d2ApiIssue" class="bg-amber-950/80 text-amber-100 border border-amber-600/50 p-3 rounded mb-3 text-sm">
            <p class="font-medium text-amber-200 mb-0.5">Destiny 2 API issue</p>
            <p>{{ d2ApiIssue }}</p>
          </div>
          <div *ngIf="dcApiFallbackNotice"
               class="bg-amber-950/80 text-amber-100 border border-amber-600/50 p-3 rounded mb-3 text-sm relative pr-9">
            <button type="button"
                    (click)="dismissDcApiFallbackNotice()"
                    class="absolute top-2 right-2 p-1 rounded text-amber-300/80 hover:text-white hover:bg-white/10"
                    aria-label="Dismiss">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <p class="font-medium text-amber-200 mb-0.5">Using Bungie API (slower)</p>
            <p>{{ dcApiFallbackNotice }}</p>
          </div>
          <div *ngIf="!isOfflineArchiveMode" class="flex flex-col sm:flex-row gap-2 sm:gap-3 items-stretch sm:items-center">
            <input type="text"
                   [(ngModel)]="searchUsername"
                   (ngModelChange)="onSearchInput($event)"
                   (keydown.enter)="handleSearchEnter($any($event))"
                   [class.border-red-500]="errorMessage"
                   class="destiny-input flex-1 px-4 py-2.5 text-sm"
                   placeholder="Type a name, then Enter to search"
                   aria-label="Destiny username">
            <button (click)="addPlayer()"
                    [disabled]="!searchUsername && searchChips.length === 0"
                    class="chronicle-toolbar-btn chronicle-toolbar-btn--primary sm:shrink-0"
                    [attr.aria-busy]="loading['search']">
              {{ loading['search'] ? 'Searching...' : 'Search' }}
            </button>
            <button type="button"
                    (click)="toggleAddMode()"
                    class="chronicle-toolbar-btn text-xs sm:shrink-0"
                    [class.chronicle-toolbar-btn--primary]="addMode">
              {{ addMode ? 'Add mode' : 'Replace mode' }}
            </button>
          </div>
          <div *ngIf="searchChips.length > 0" class="flex flex-wrap gap-2 mt-2">
            <span *ngFor="let chip of searchChips; let i = index" class="destiny-chip">
              {{ chip }}
              <button (click)="removeSearchChip(i)" class="text-slate-400 hover:text-white" aria-label="Remove name">✕</button>
            </span>
          </div>
          <div *ngIf="errorMessage" class="mt-2 text-sm text-red-400">{{ errorMessage }}</div>
        </div>

        <div class="dc-dossier-hdr__tabs" id="dc-tab-panel-top">
          <nav class="destiny-tab-nav md:gap-1 w-full" role="tablist" aria-label="Activity views"
               data-dc-engram-target="tabs">
            <button
              type="button"
              (click)="onTabChange('activities')"
              class="main-tab"
              [ngClass]="{ 'main-tab--active': activeTab === 'activities' }"
              role="tab"
              [attr.aria-selected]="activeTab === 'activities'"
              [title]="uiI18n.t('tab.activities')">
              📊 {{ uiI18n.t('tab.activities') }}
            </button>
            <button
              type="button"
              (click)="onTabChange('firsts')"
              class="main-tab"
              [ngClass]="{ 'main-tab--active': activeTab === 'firsts' }"
              role="tab"
              [attr.aria-selected]="activeTab === 'firsts'"
              [title]="uiI18n.t('tab.firsts')">
              🏆 {{ uiI18n.t('tab.firsts') }}
            </button>
            <button
              type="button"
              (click)="onTabChange('titles')"
              class="main-tab"
              [ngClass]="{ 'main-tab--active': activeTab === 'titles' }"
              role="tab"
              [attr.aria-selected]="activeTab === 'titles'"
              title="Titles">
              🎖️ {{ uiI18n.t('tab.titles') }}
            </button>
            <button
              type="button"
              (click)="onTabChange('accomplishments')"
              class="main-tab"
              [ngClass]="{ 'main-tab--active': activeTab === 'accomplishments' }"
              role="tab"
              [attr.aria-selected]="activeTab === 'accomplishments'"
              [title]="uiI18n.t('tab.accomplishments')">
              🏅 {{ uiI18n.t('tab.accomplishments') }}
            </button>
            <button
              type="button"
              (click)="onTabChange('breakdown')"
              class="main-tab"
              [ngClass]="{ 'main-tab--active': activeTab === 'breakdown' }"
              role="tab"
              [attr.aria-selected]="activeTab === 'breakdown'"
              [title]="uiI18n.t('tab.breakdown')">
              📋 {{ uiI18n.t('tab.breakdown') }}
            </button>
            <button
              type="button"
              (click)="onTabChange('heatmap')"
              class="main-tab"
              [ngClass]="{ 'main-tab--active': activeTab === 'heatmap' }"
              role="tab"
              [attr.aria-selected]="activeTab === 'heatmap'"
              [title]="uiI18n.t('tab.heatmap')">
              🔥 {{ uiI18n.t('tab.heatmap') }}
            </button>
            <button
              *ngIf="enablePlayedWith"
              type="button"
              (click)="onTabChange('playedWith')"
              class="main-tab"
              [ngClass]="{ 'main-tab--active': activeTab === 'playedWith' }"
              role="tab"
              [attr.aria-selected]="activeTab === 'playedWith'"
              [title]="uiI18n.t('tab.playedWith')">
              🤝 {{ uiI18n.t('tab.playedWith') }}
            </button>
          </nav>
        </div>
      </header>

      <div *ngIf="textView.enabled() && selectedPlayers.length === 0"
           class="dc-dossier-hdr__tabs mb-4"
           id="dc-tab-panel-top-text">
        <nav class="destiny-tab-nav md:gap-1 w-full" role="tablist" aria-label="Activity views"
             data-dc-engram-target="tabs">
          <button type="button" (click)="onTabChange('activities')" class="main-tab"
                  [ngClass]="{ 'main-tab--active': activeTab === 'activities' }" role="tab"
                  [attr.aria-selected]="activeTab === 'activities'">📊 {{ uiI18n.t('tab.activities') }}</button>
          <button type="button" (click)="onTabChange('firsts')" class="main-tab"
                  [ngClass]="{ 'main-tab--active': activeTab === 'firsts' }" role="tab"
                  [attr.aria-selected]="activeTab === 'firsts'">🏆 {{ uiI18n.t('tab.firsts') }}</button>
          <button type="button" (click)="onTabChange('titles')" class="main-tab"
                  [ngClass]="{ 'main-tab--active': activeTab === 'titles' }" role="tab"
                  [attr.aria-selected]="activeTab === 'titles'">🎖️ {{ uiI18n.t('tab.titles') }}</button>
          <button type="button" (click)="onTabChange('accomplishments')" class="main-tab"
                  [ngClass]="{ 'main-tab--active': activeTab === 'accomplishments' }" role="tab"
                  [attr.aria-selected]="activeTab === 'accomplishments'">🏅 {{ uiI18n.t('tab.accomplishments') }}</button>
          <button type="button" (click)="onTabChange('breakdown')" class="main-tab"
                  [ngClass]="{ 'main-tab--active': activeTab === 'breakdown' }" role="tab"
                  [attr.aria-selected]="activeTab === 'breakdown'">📋 {{ uiI18n.t('tab.breakdown') }}</button>
          <button type="button" (click)="onTabChange('heatmap')" class="main-tab"
                  [ngClass]="{ 'main-tab--active': activeTab === 'heatmap' }" role="tab"
                  [attr.aria-selected]="activeTab === 'heatmap'">🔥 {{ uiI18n.t('tab.heatmap') }}</button>
          <button *ngIf="enablePlayedWith" type="button" (click)="onTabChange('playedWith')" class="main-tab"
                  [ngClass]="{ 'main-tab--active': activeTab === 'playedWith' }" role="tab"
                  [attr.aria-selected]="activeTab === 'playedWith'">🤝 {{ uiI18n.t('tab.playedWith') }}</button>
        </nav>
      </div>

      <div *ngIf="showLoadingModal && !decryptLanding.visible() && !decryptLanding.ownsLoad()"
           class="loading-profiles-strip loading-profiles-strip--engram w-full">
        <div class="flex items-center gap-3 min-w-0">
          <dc-load-engram
            layout="row"
            size="md"
            [centered]="false"
            [hasProjected]="true"
            [progress]="getOverallProgress()"
            [caption]="decryptingCaption"
            [subline]="decryptingSubline">
            <div class="engram-decrypt-bar destiny-progress-track h-1.5 mt-2 max-w-xs">
              <div class="destiny-progress-fill h-full rounded-full" [style.width.%]="getOverallProgress()"></div>
            </div>
          </dc-load-engram>
          <button type="button" (click)="loadingPanelExpanded = !loadingPanelExpanded"
                  class="chronicle-toolbar-btn px-2 py-1 text-xs shrink-0"
                  [attr.aria-expanded]="loadingPanelExpanded">
            {{ loadingPanelExpanded ? 'Less' : 'Details' }}
          </button>
          <button *ngIf="!isLoadingComplete"
                  type="button"
                  (click)="stopProfileLoading()"
                  class="chronicle-toolbar-btn px-2 py-1 text-xs shrink-0 text-amber-200 border border-amber-600/40"
                  title="Stop loading and search for someone else">
            Stop
          </button>
          <button type="button" (click)="closeLoadingModal()" class="text-slate-500 hover:text-white p-1 shrink-0" aria-label="Dismiss">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div *ngIf="loadingPanelExpanded" class="mt-2 pt-2 border-t border-[rgba(201,162,39,0.12)] space-y-1 max-h-28 overflow-y-auto">
          <div *ngFor="let status of accountLoadingStatuses"
               class="flex items-center justify-between gap-2 px-1 py-0.5 text-xs rounded hover:bg-white/5">
            <div class="flex items-center gap-1.5 min-w-0 truncate">
              <img [src]="getGameIconUrl(status.game)" class="w-3 h-3 shrink-0" alt="" />
              <span class="text-slate-200 truncate">{{ status.displayName }}</span>
              <span class="text-slate-500 shrink-0">{{ formatStatusDisplay(status.status) }}</span>
            </div>
            <span *ngIf="status.status === 'complete'" class="text-green-400">✓</span>
            <span *ngIf="status.status === 'error'" class="text-red-400">✗</span>
          </div>
          <p *ngIf="loadingProgress && !isLoadingComplete" class="text-[11px] text-slate-500 truncate px-1" [title]="loadingProgress.message">
            {{ loadingProgress.phase }}: {{ loadingProgress.message }}
          </p>
        </div>
      </div>

      <div class="dc-flow-main flex-1 min-w-0 w-full"
           data-dc-engram-target="main">
        <div *ngIf="(pgcrPrefetchProgress$ | async) as prefetch" class="mb-3">
          <div *ngIf="prefetch.inProgress"
               class="destiny-surface-panel text-center text-slate-400 py-3 px-4 flex flex-col sm:flex-row items-center justify-center gap-2">
            <div class="flex items-center gap-2">
              <app-destiny-loader size="sm"></app-destiny-loader>
              <span>Loading additional report data in background…</span>
            </div>
            <div *ngIf="prefetch.total > 0" class="text-xs text-slate-500">
              Batch {{ prefetch.current }} of {{ prefetch.total }}
            </div>
          </div>
        </div>

        <div *ngIf="activeTab === 'activities'" class="dc-flow-activities-host">
          <app-activities-tab></app-activities-tab>
        </div>

        <div *ngIf="activeTab === 'breakdown'">
          @defer (on immediate) {
            <app-breakdown-tab></app-breakdown-tab>
          } @loading (after 150ms) { <div class="flex justify-center py-6"><app-destiny-loader size="sm"></app-destiny-loader></div> }
        </div>

        <div *ngIf="activeTab === 'firsts' && selectedPlayers.length > 0">
          @defer (on immediate) {
            <app-firsts-tab></app-firsts-tab>
          } @loading (after 150ms) { <div class="flex justify-center py-6"><app-destiny-loader size="sm"></app-destiny-loader></div> }
        </div>

        <div *ngIf="activeTab === 'titles'">
          @defer (on immediate) {
            <app-titles-tab></app-titles-tab>
          } @loading (after 150ms) { <div class="flex justify-center py-6"><app-destiny-loader size="sm"></app-destiny-loader></div> }
        </div>

        <div *ngIf="activeTab === 'accomplishments' && selectedPlayers.length > 0">
          @defer (on immediate) {
            <app-accomplishments-tab></app-accomplishments-tab>
          } @loading (after 150ms) { <div class="flex justify-center py-6"><app-destiny-loader size="sm"></app-destiny-loader></div> }
        </div>

        <div *ngIf="activeTab === 'heatmap'">
          @defer (on immediate) {
            <app-activity-heatmap
              [membershipIds]="heatmapMembershipIds"
              [accountKeys]="heatmapAccountKeys"
              [characterMeta]="heatmapCharacterMeta"
              (navigateToDate)="onHeatmapDateNavigate($event)"></app-activity-heatmap>
          } @loading (after 150ms) { <div class="flex justify-center py-6"><app-destiny-loader size="sm"></app-destiny-loader></div> }
        </div>

        <div *ngIf="enablePlayedWith && activeTab === 'playedWith' && selectedPlayers.length > 0">
          @if (playedWithAccounts.length) {
            @defer (on immediate) {
              <app-played-with [accounts]="playedWithAccounts"></app-played-with>
            } @loading (after 150ms) { <div class="flex justify-center py-6"><app-destiny-loader size="sm"></app-destiny-loader></div> }
          } @else {
            <p class="text-sm text-slate-400">{{ uiI18n.t('playedWith.d2Only') }}</p>
          }
        </div>
      </div>
    </div>
  </div>

<!-- Favorites Modal -->
<div *ngIf="showFavoritesModal" class="destiny-modal-overlay"
     (pointerdown)="onModalOverlayPointerDown($event)"
     (click)="closeModalOnBackdrop($event)"
     role="dialog" aria-modal="true" aria-labelledby="favorites-modal-title">
  <div class="destiny-modal-panel max-w-2xl" (pointerdown)="$event.stopPropagation()" (click)="$event.stopPropagation()">
    <div class="flex items-center justify-between mb-4">
      <h2 id="favorites-modal-title" class="text-xl font-bold text-white">Favorites</h2>
      <button (click)="showFavoritesModal = false" class="text-slate-400 hover:text-white text-xl"
              aria-label="Close favorites modal">
        ✕
      </button>
    </div>
    
    <div *ngIf="favoriteAccounts.length === 0" class="text-center py-8">
      <div class="text-slate-400 mb-2">No favorites saved yet</div>
      <div class="text-sm text-slate-500">Search for profiles and click the star to save them as favorites</div>
    </div>
    
    <div *ngIf="favoriteAccounts.length > 0" class="space-y-3 max-h-96 overflow-y-auto">
      <div *ngFor="let fav of favoriteAccounts" 
           class="flex items-center justify-between p-3 destiny-modal-section hover:border-[rgba(245,197,66,0.3)] transition-colors">
        <div class="flex items-center gap-3 flex-1 min-w-0">
          <img [src]="getPlatformIconUrl(fav.membershipType)" class="w-5 h-5 flex-shrink-0" alt="platform" />
          <img [src]="getGameIconUrl(fav.game)" class="w-5 h-5 flex-shrink-0" alt="game" />
          <div class="min-w-0">
            <div class="text-white font-medium truncate" [title]="fav.displayName">
              {{ fav.displayName }}
            </div>
            <div class="text-sm text-slate-400">{{ fav.platform }} • {{ fav.game }}</div>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <button (click)="loadMultipleFavorites([fav]); showFavoritesModal = false" 
                  class="px-3 py-1.5 destiny-btn-primary text-sm">
            Load
          </button>
                              <button (click)="removeFavorite(fav.membershipId, fav.game, fav.membershipType)" 
                            class="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white rounded text-sm">
                      Remove
                    </button>
        </div>
      </div>
    </div>
    
    <div *ngIf="favoriteAccounts.length > 0" class="mt-4 pt-4 border-t border-slate-600">
      <button (click)="loadMultipleFavorites(favoriteAccounts); showFavoritesModal = false" 
              class="w-full px-4 py-2 destiny-btn-primary font-medium">
        Load All Favorites
      </button>
    </div>
  </div>
</div>

<!-- Search Results Modal -->
<div *ngIf="showPlatformPicker" class="destiny-modal-overlay"
     (pointerdown)="onModalOverlayPointerDown($event)"
     (click)="closeModalOnBackdrop($event)"
     role="dialog" aria-modal="true" aria-labelledby="search-modal-title">
  <div class="destiny-modal-panel max-w-2xl" (pointerdown)="$event.stopPropagation()" (click)="$event.stopPropagation()">
    <div class="flex items-center justify-between mb-4">
      <h2 id="search-modal-title" class="text-xl font-bold text-white">Select Accounts</h2>
      <button type="button" (click)="showPlatformPicker = false" class="text-slate-400 hover:text-white text-xl"
              aria-label="Close modal">
        ✕
      </button>
    </div>
    
    <!-- Mode Indicator -->
    <div *ngIf="selectedPlayers.length > 0" class="mb-4 p-3 rounded text-sm" 
         [ngClass]="addMode ? 'destiny-banner-success' : 'destiny-banner-info'">
      <div class="font-semibold mb-1">
        {{ addMode ? '✓ Add Mode Active' : '⚠ Replace Mode Active' }}
      </div>
      <div class="text-xs opacity-90">
        {{ addMode ? 'Selected accounts will be added to your current display.' : 'Selected accounts will replace your current display.' }}
      </div>
    </div>
    
    <!-- Selection Controls -->
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <button (click)="selectAllPlayersInModal()" 
                class="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded text-sm">
          Select All
        </button>
        <button (click)="clearModalSelection()" 
                class="px-3 py-1.5 destiny-btn-secondary text-sm">
          Clear Selection
        </button>
        <button (click)="addSelectedToFavorites()" 
                [disabled]="getSelectedCount() === 0"
                class="px-3 py-1.5 destiny-btn-outline disabled:opacity-50 disabled:cursor-not-allowed text-sm">
          Add to Favorites
        </button>
      </div>
      <div class="flex items-center gap-4">
        <!-- Include Linked Accounts Toggle -->

      <div class="text-sm text-slate-400">
        {{ getSelectedCount() }} of {{ getTotalCount() }} selected · click a name to toggle
      </div>
      </div>
    </div>
    
    <div class="space-y-4 max-h-96 overflow-y-auto">
      <!-- Cross Save Option -->
      <div *ngIf="crossSavePlayer" class="destiny-modal-section">
        <h3 class="text-lg font-semibold mb-2" style="color: var(--destiny-gold)">Cross Save Account</h3>
        <button type="button"
                class="modal-account-pick"
                [class.modal-account-pick--selected]="isPlayerSelected(crossSavePlayer)"
                [class.modal-account-pick--empty]="crossSavePlayer.hasGames === false"
                [attr.aria-pressed]="isPlayerSelected(crossSavePlayer)"
                (click)="togglePlayerSelection(crossSavePlayer, $event)">
          <img [src]="getPlatformIconUrl(crossSavePlayer.membershipType)" class="w-5 h-5 shrink-0" alt="" />
          <div class="modal-account-pick-body">
            <div class="modal-account-pick-name">{{ crossSavePlayer.displayName }}</div>
            <div class="modal-account-pick-meta">Cross Save · {{ searchAccountMeta(crossSavePlayer) }}</div>
          </div>
          <span class="modal-account-pick-badge">{{ isPlayerSelected(crossSavePlayer) ? 'Selected' : 'Select' }}</span>
        </button>
      </div>
      
      <!-- D2 Results (exclude Cross Save primary — already listed above) -->
      <div *ngIf="d2ModalAccounts.length > 0" class="destiny-modal-section">
        <h3 class="text-lg font-semibold text-blue-300 mb-2">Destiny 2 Accounts</h3>
        <div class="space-y-2">
          <button type="button"
                  *ngFor="let player of d2ModalAccounts"
                  class="modal-account-pick"
                  [class.modal-account-pick--selected]="isPlayerSelected(player)"
                  [class.modal-account-pick--empty]="player.hasGames === false"
                  [attr.aria-pressed]="isPlayerSelected(player)"
                  (click)="togglePlayerSelection(player, $event)">
            <img [src]="getPlatformIconUrl(player.membershipType)" class="w-5 h-5 shrink-0" alt="" />
            <div class="modal-account-pick-body">
              <div class="modal-account-pick-name">{{ player.displayName }}</div>
              <div class="modal-account-pick-meta">{{ searchAccountMeta(player) }}</div>
            </div>
            <span class="modal-account-pick-badge">{{ isPlayerSelected(player) ? 'Selected' : 'Select' }}</span>
          </button>
        </div>
      </div>
      
      <!-- D1 Results -->
      <div *ngIf="d1SearchResults.length > 0" class="destiny-modal-section">
        <h3 class="text-lg font-semibold text-slate-300 mb-2">Destiny 1 Accounts</h3>
        <div class="space-y-2">
          <button type="button"
                  *ngFor="let player of d1SearchResults"
                  class="modal-account-pick"
                  [class.modal-account-pick--selected]="isPlayerSelected(player)"
                  [class.modal-account-pick--empty]="player.hasGames === false"
                  [attr.aria-pressed]="isPlayerSelected(player)"
                  (click)="togglePlayerSelection(player, $event)">
            <img [src]="getPlatformIconUrl(player.membershipType)" class="w-5 h-5 shrink-0" alt="" />
            <div class="modal-account-pick-body">
              <div class="modal-account-pick-name">{{ player.displayName }}</div>
              <div class="modal-account-pick-meta">{{ searchAccountMeta(player) }}</div>
            </div>
            <span class="modal-account-pick-badge">{{ isPlayerSelected(player) ? 'Selected' : 'Select' }}</span>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Action Buttons -->
    <div class="flex items-center justify-between mt-6 pt-4 border-t border-slate-600">
      <button type="button" (click)="showPlatformPicker = false" 
              class="px-4 py-2 destiny-btn-secondary">
        Cancel
      </button>
      <button type="button" (click)="loadSelectedPlayers()" 
              [disabled]="getSelectedCount() === 0"
              class="px-4 py-2 destiny-btn-primary disabled:opacity-50 disabled:cursor-not-allowed font-medium">
        Load Selected ({{ getSelectedCount() }})
      </button>
    </div>
  </div>
</div>

<!-- Advanced Options (Less Prominent) -->
<div class="w-full flex justify-end mt-12 px-4">
  <details class="group">
    <summary class="text-xs text-slate-300 hover:text-slate-200 cursor-pointer list-none">
      ⚙️ Advanced Options
    </summary>
    <div class="mt-2 p-3 destiny-surface-panel">
      <p class="text-xs text-slate-400 mb-3">
        Clear all cached activity data from your browser. This will require re-downloading all data on your next visit.
      </p>
  <button
        (click)="confirmClearData()"
        class="px-3 py-1.5 bg-red-700 hover:bg-red-600 text-white rounded text-xs transition-colors"
    title="This will remove all stored activity data from your browser.">
        Clear Cached Data
  </button>
    </div>
  </details>
</div> 


  <!-- Share & Archive menu (modal — avoids clipping in date panel) -->
  <div *ngIf="showShareDropdown" class="destiny-modal-overlay" (click)="showShareDropdown = false"
       role="dialog" aria-modal="true" aria-label="Share and archive">
    <div class="destiny-modal-panel share-archive-panel max-w-md w-full p-0 overflow-hidden" (click)="$event.stopPropagation()">
      <div class="share-archive-panel__header px-4 py-3 border-b border-[rgba(255,208,32,0.22)]">
        <h3 class="text-lg font-semibold text-[var(--destiny-gold-bright)] m-0 font-d2-headline">Share &amp; Archive</h3>
      </div>
      <div class="share-archive-panel__body max-h-[min(70vh,28rem)] overflow-y-auto overscroll-contain py-1">
        <button type="button" (click)="captureScreenshot(); showShareDropdown = false" class="destiny-dropdown-item flex items-center gap-2">
          <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
          Take Screenshot
        </button>
        <button type="button" (click)="copyDataToClipboard(); showShareDropdown = false" class="destiny-dropdown-item flex items-center gap-2">
          <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>
          Copy Data (JSON)
        </button>
        <button type="button" (click)="exportCurrentDate(); showShareDropdown = false" class="destiny-dropdown-item flex items-center gap-2">
          <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
          Export This Date
        </button>
        <button type="button" (click)="openExportOptionsDialog(); showShareDropdown = false" class="destiny-dropdown-item flex items-center gap-2">
          <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
          Advanced Export
        </button>
        <div class="border-t border-[rgba(255,208,32,0.18)] my-1"></div>
        <p class="px-4 py-1.5 text-[10px] uppercase tracking-wider text-[var(--destiny-flame-bright)] font-semibold">Offline archive</p>
        <button *ngIf="!isOfflineArchiveMode" type="button"
                [disabled]="archiveExporting || selectedPlayers.length === 0"
                (click)="saveForOfflineOnDevice(true); showShareDropdown = false"
                class="destiny-dropdown-item text-left">
          {{ uiI18n.t('archive.saveOnDevice') }}
        </button>
        <button type="button"
                (click)="toggleArchiveBackupOptions()"
                class="destiny-dropdown-item text-left text-slate-400">
          {{ uiI18n.t('archive.backupSection') }} {{ showArchiveBackupOptions ? '▲' : '▼' }}
        </button>
        <ng-container *ngIf="showArchiveBackupOptions">
          <button type="button" [disabled]="archiveExporting || selectedPlayers.length === 0"
                  (click)="buildOfflineArchive(true); showShareDropdown = false" class="destiny-dropdown-item text-left pl-6">{{ uiI18n.t('archive.build') }}</button>
          <button type="button" [disabled]="archiveExporting || selectedPlayers.length === 0"
                  (click)="buildOfflineArchive(false); showShareDropdown = false" class="destiny-dropdown-item text-left pl-6">{{ uiI18n.t('archive.buildLite') }}</button>
          <button type="button" [disabled]="archiveExporting || selectedPlayers.length === 0"
                  (click)="buildHtmlArchiveReport(); showShareDropdown = false" class="destiny-dropdown-item text-left pl-6">{{ uiI18n.t('archive.buildHtmlReport') }}</button>
          <button type="button" [disabled]="archiveExporting"
                  (click)="triggerArchiveImport(); showShareDropdown = false" class="destiny-dropdown-item text-left pl-6">{{ uiI18n.t('archive.open') }}</button>
          <button *ngIf="!isOfflineArchiveMode && selectedPlayers.length > 0" type="button"
                  [disabled]="archiveExporting" (click)="updateOfflineArchive(); showShareDropdown = false"
                  class="destiny-dropdown-item text-left pl-6">{{ uiI18n.t('archive.update') }}</button>
        </ng-container>
      </div>
      <div class="share-archive-panel__footer px-4 py-3 border-t border-[rgba(255,208,32,0.18)]">
        <button type="button" (click)="showShareDropdown = false" class="chronicle-toolbar-btn w-full">Close</button>
      </div>
    </div>
  </div>

  <!-- Export Options Dialog -->
  <app-export-options-dialog *ngIf="showExportDialog" [selectedDate]="selectedDate" (close)="showExportDialog = false" (export)="handleExportOptions($event)"></app-export-options-dialog>

  <app-decrypted-share-card
    [open]="shareMemoryOpen"
    [memory]="shareMemoryItem"
    [flavorLine]="shareMemoryFlavor"
    (close)="closeShareMemoryCard()">
  </app-decrypted-share-card>
